import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Ban, MoreVertical, Search, Filter, Download, X, Save, Edit2, Trash2, UserPlus, AlertTriangle, ShieldAlert, Clock, PlayCircle, Wallet, RefreshCw, CheckCircle, Sparkles, Award, Globe, Building2, Coins, UserCheck, Crown, Lock } from 'lucide-react';
import { usersAPI, authAPI, walletsAPI, agenciesAPI } from '../utils/api';
import { FilterCountrySelect, SearchableCountrySelect, CountryBadge } from '../components/SearchableCountrySelect';
import axios from 'axios';
import constant from '../constants/constant';

const ROLE_RANKS = {
  'OWNER': 100,
  'SUPER_ADMIN': 80,
  'ADMIN': 60,
  'MANAGER': 40,
  'BD': 30,
  'AGENCY': 20,
  'HOST': 15,
  'USER': 10
};

const UsersManagement = () => {
  const token = sessionStorage.getItem('adminToken');
  const userinfo = React.useMemo(() => {
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [token]);

  const userType = userinfo?.user_type || 'ADMIN';
  const callerRank = ROLE_RANKS[userType] || 0;

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [countryFilter, setCountryFilter] = useState('ALL');
  const [users, setUsers] = useState([]);
  const [agenciesList, setAgenciesList] = useState([]);
  const [walletsMap, setWalletsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [toast, setToast] = useState({ message: '', type: '', visible: false });
  const showToast = (message, type = 'success') => {
    setToast({ message, type, visible: true });
    setTimeout(() => setToast({ message: '', type: '', visible: false }), 3000);
  };

  // Helper to ensure only Active & Approved BDs appear in selection lists
  const isBdActiveAndApproved = (u, excludeId = null) => {
    if (!u || u.user_type !== 'BD') return false;
    if (excludeId && u.id === excludeId) return false;
    const isBanned = Boolean(u.is_ban) || (u.banned_until && new Date(u.banned_until) > new Date());
    if (u.is_active === false || isBanned) return false;
    if (u.status && u.status !== 'approved' && u.status !== 'active') return false;
    if (u.bd_invitation_status && u.bd_invitation_status !== 'accepted') return false;
    return true;
  };
  
  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    phone: '',
    user_type: 'USER',
    country: 'India',
    is_active: true,
    user_id: '',
    diamond_salary: '',
    is_seller: false,
    agency_id: '',
    agency_name: '',
    bd_user_id: '',
    manager_id: '',
    transfer_to_agency_id: ''
  });

  // Warning Modal
  const [isWarnModalOpen, setIsWarnModalOpen] = useState(false);
  const [targetUser, setTargetUser] = useState(null);
  const [warnReason, setWarnReason] = useState('');

  // Ban Modal
  const [isBanModalOpen, setIsBanModalOpen] = useState(false);
  const [banTargetUser, setBanTargetUser] = useState(null);
  const [banDurationType, setBanDurationType] = useState('days'); // 'hours' | 'days' | 'permanent'
  const [banDurationValue, setBanDurationValue] = useState(7);
  const [banReason, setBanReason] = useState('');
  const [banLoading, setBanLoading] = useState(false);

  // Tx Logs Modal
  const [isTxModalOpen, setIsTxModalOpen] = useState(false);
  const [txUser, setTxUser] = useState(null);
  const [txLogs, setTxLogs] = useState([]);
  const [txLoading, setTxLoading] = useState(false);

  // User Level Edit Modal
  const [isLevelModalOpen, setIsLevelModalOpen] = useState(false);
  const [levelUser, setLevelUser] = useState(null);
  const [userWealthLevel, setUserWealthLevel] = useState(1);
  const [userCharmLevel, setUserCharmLevel] = useState(1);
  const [levelSaving, setLevelSaving] = useState(false);

  const handleOpenLevelModal = (user) => {
    setLevelUser(user);
    setUserWealthLevel(user.wealth_level || 1);
    setUserCharmLevel(user.charm_level || 1);
    setIsLevelModalOpen(true);
  };

  const handleSaveUserLevel = async (e) => {
    e.preventDefault();
    if (!levelUser) return;
    setLevelSaving(true);
    try {
      const res = await usersAPI.updateUserLevel(levelUser.id, {
        wealth_level: userWealthLevel,
        charm_level: userCharmLevel
      });
      showToast(res.data?.message || 'User level updated and tier rewards granted successfully!');
      setIsLevelModalOpen(false);
      fetchUsersAndWallets();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Error updating user level', 'error');
    } finally {
      setLevelSaving(false);
    }
  };

  // Custom ID Modal
  const [isCustomIdModalOpen, setIsCustomIdModalOpen] = useState(false);
  const [customIdUser, setCustomIdUser] = useState(null);
  const [customIdValue, setCustomIdValue] = useState('');
  const [customIdLoading, setCustomIdLoading] = useState(false);

  const handleOpenCustomIdModal = (user) => {
    setCustomIdUser(user);
    setCustomIdValue(user.xoxo_id || user.user_id || '');
    setIsCustomIdModalOpen(true);
  };

  const handleSaveCustomId = async (e, autoGen = false) => {
    if (e) e.preventDefault();
    if (!customIdUser) return;
    setCustomIdLoading(true);
    try {
      const payload = autoGen ? { auto_generate: true } : { custom_id: customIdValue };
      const res = await usersAPI.customizeId(customIdUser.id, payload);
      showToast(res.data?.message || 'User ID customized successfully!');
      setIsCustomIdModalOpen(false);
      fetchUsersAndWallets();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Error customizing user ID', 'error');
    } finally {
      setCustomIdLoading(false);
    }
  };

  const getTierInfo = (level) => {
    const lvl = parseInt(level) || 1;
    if (lvl <= 20) return { name: 'Bronze Tier', color: 'text-amber-700 bg-amber-50 border-amber-200', range: 'Lv 1-20' };
    if (lvl <= 40) return { name: 'Silver Tier', color: 'text-slate-700 bg-slate-100 border-slate-300', range: 'Lv 21-40' };
    if (lvl <= 60) return { name: 'Gold Tier', color: 'text-yellow-700 bg-yellow-50 border-yellow-300', range: 'Lv 41-60' };
    if (lvl <= 80) return { name: 'Platinum Tier', color: 'text-cyan-700 bg-cyan-50 border-cyan-300', range: 'Lv 61-80' };
    return { name: 'Diamond Tier', color: 'text-purple-700 bg-purple-50 border-purple-300', range: 'Lv 81-100' };
  };

  const countries = [
    { name: 'India', flag: '🇮🇳' },
    { name: 'USA', flag: '🇺🇸' },
    { name: 'UK', flag: '🇬🇧' },
    { name: 'Canada', flag: '🇨🇦' },
    { name: 'Australia', flag: '🇦🇺' },
    { name: 'UAE', flag: '🇦🇪' },
  ];

  useEffect(() => {
    fetchUsersAndWallets();
  }, []);

  const fetchUsersAndWallets = async () => {
    setLoading(true);
    try {
      const [usersRes, walletsRes, agenciesRes] = await Promise.all([
        usersAPI.getUsers(),
        walletsAPI.getWallets().catch(() => ({ data: { data: [] } })),
        agenciesAPI.getAgencies().catch(() => ({ data: [] }))
      ]);
      
      setUsers(usersRes.data || []);
      const agList = Array.isArray(agenciesRes.data) ? agenciesRes.data : (agenciesRes.data?.data || []);
      setAgenciesList(agList);
      
      const walletsData = walletsRes.data?.data || [];
      const wMap = {};
      walletsData.forEach(w => {
        wMap[w.user_id] = { balance: w.balance, diamond_balance: w.diamond_balance };
      });
      setWalletsMap(wMap);
      setError(null);
    } catch (err) {
      console.error('Error fetching users and wallets:', err);
      setError('Failed to load user directories. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };
      if (name === 'user_type' && value === 'AGENCY' && editingUser && next.bd_user_id === editingUser.id) {
        next.bd_user_id = '';
      }
      return next;
    });
  };

  const openAddModal = () => {
    setEditingUser(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      phone: '',
      user_type: 'USER',
      country: '',
      is_active: true,
      user_id: '',
      diamond_salary: '',
      is_seller: false,
      agency_id: '',
      agency_name: '',
      bd_user_id: '',
      manager_id: '',
      transfer_to_agency_id: '',
      transfer_to_bd_id: '',
      transfer_to_manager_id: ''
    });
    setIsModalOpen(true);
  };

  const openEditModal = (user, overrideRole = null) => {
    const targetRank = ROLE_RANKS[user?.user_type] || 10;
    if (callerRank <= targetRank && user?.id !== userinfo?.id) {
      showToast(`Permission denied: You cannot edit an account with rank equal to or higher than your own (${user?.user_type || 'User'}).`, 'error');
      return;
    }
    setEditingUser(user);
    const newRole = overrideRole || user.user_type || 'USER';
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      password: '',
      phone: user.phone || '',
      user_type: newRole,
      country: user.country || '',
      is_active: user.is_active ?? true,
      user_id: user.user_id || '',
      diamond_salary: user.diamond_salary || '',
      is_seller: Boolean(user.is_seller),
      agency_id: user.host_agency_id || user.agency_id || '',
      agency_name: user.owned_agency_name || '',
      bd_user_id: (user.bd_user_id && user.bd_user_id !== user.id) ? user.bd_user_id : '',
      manager_id: user.manager_id || '',
      transfer_to_agency_id: '',
      transfer_to_bd_id: '',
      transfer_to_manager_id: ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (payload.user_type === 'AGENCY' && (!payload.agency_name || !payload.agency_name.trim())) {
        showToast('Please enter an Agency Name!', 'error');
        return;
      }
      if (editingUser && payload.bd_user_id === editingUser.id) {
        payload.bd_user_id = null;
      }
      // Convert diamond_salary to 0 if empty
      if (payload.diamond_salary === '' || payload.diamond_salary === null) {
        payload.diamond_salary = 0;
      }
      if (payload.user_id === '') {
        delete payload.user_id;
      }
      if (payload.phone === '') {
        delete payload.phone;
      }
      if (payload.agency_id === '') {
        payload.agency_id = null;
      }
      if (!payload.country) {
        delete payload.country;
      }
      
      if (editingUser) {
        if (!payload.password) delete payload.password;
        await usersAPI.updateUser(editingUser.id, payload);
        showToast('User updated successfully');
      } else {
        await usersAPI.createUser(payload);
        showToast('User created successfully');
      }
      setIsModalOpen(false);
      fetchUsersAndWallets();
    } catch (err) {
      showToast(err.response?.data?.message || err.response?.data?.errors || err.message || 'Error saving user', 'error');
    }
  };

  const handleStripRole = async (user) => {
    const targetRank = ROLE_RANKS[user?.user_type] || 10;
    if (callerRank <= targetRank) {
      showToast(`Permission denied: You cannot modify privileges of an account with equal or higher rank (${user?.user_type}).`, 'error');
      return;
    }
    const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'User';
    if (!window.confirm(`Are you sure you want to revoke Admin/Super Admin privileges from "${userName}" and demote them to a regular USER?`)) {
      return;
    }
    try {
      await usersAPI.updateUser(user.id, {
        user_type: 'USER',
        is_seller: false,
        agency_id: null
      });
      showToast(`Administrative privileges for "${userName}" have been revoked. Account demoted to USER.`);
      fetchUsersAndWallets();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Error removing role', 'error');
    }
  };

  const handleDelete = async (userOrId) => {
    const user = typeof userOrId === 'object' ? userOrId : users.find(u => u.id === userOrId);
    const targetRank = ROLE_RANKS[user?.user_type] || 10;
    if (callerRank <= targetRank) {
      showToast(`Permission denied: You cannot delete an account with rank equal to or higher than your own (${user?.user_type || 'User'}).`, 'error');
      return;
    }
    const userName = user ? (`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'User') : 'this user';
    const userEmail = user?.email ? ` (${user.email})` : '';
    const userId = user ? user.id : userOrId;
    const isSuperAdmin = user?.user_type === 'SUPER_ADMIN';

    if (isSuperAdmin) {
      const choice = window.confirm(
        `User "${userName}" is a SUPER ADMIN account.\n\n` +
        `• Click OK to permanently delete this user account.\n` +
        `• Click Cancel if you only want to revoke their role and demote them to a regular user.`
      );
      if (!choice) {
        if (window.confirm(`Would you like to revoke Super Admin privileges from "${userName}" and demote them to a regular USER?`)) {
          await handleStripRole(user);
        }
        return;
      }
    } else {
      if (!window.confirm(`Are you sure you want to permanently delete user "${userName}"${userEmail}? All associated wallets, records, and permissions will be deleted. This action cannot be undone.`)) {
        return;
      }
    }

    try {
      const res = await usersAPI.deleteUser(userId);
      showToast(res.data?.message || 'User deleted successfully');
      fetchUsersAndWallets();
    } catch (err) {
      const errData = err.response?.data;
      const msg = errData?.message || err.message || 'Error deleting user';
      if (err.response?.status === 403 || errData?.can_demote || isSuperAdmin) {
        if (window.confirm(`${msg}\n\nWould you like to revoke Admin privileges from this user and demote them to a regular USER instead?`)) {
          await handleStripRole(user);
          return;
        }
      }
      showToast(msg, 'error');
    }
  };

  // BAN / UNBAN
  const handleOpenBanModal = (user) => {
    const targetRank = ROLE_RANKS[user?.user_type] || 10;
    if (callerRank <= targetRank) {
      showToast(`Permission denied: You cannot ban an account with rank equal to or higher than your own (${user?.user_type}).`, 'error');
      return;
    }
    setBanTargetUser(user);
    setBanDurationType('days');
    setBanDurationValue(7);
    setBanReason('');
    setIsBanModalOpen(true);
  };

  const handleExecuteBan = async (e) => {
    e.preventDefault();
    if (!banTargetUser) return;
    setBanLoading(true);
    try {
      const payload = {
        ban: true,
        duration_type: banDurationType,
        duration_value: banDurationType === 'permanent' ? 0 : parseInt(banDurationValue) || 1,
        reason: banReason.trim() || 'Violated community guidelines'
      };
      await usersAPI.banUser(banTargetUser.id, payload);
      showToast(`User ${banTargetUser.first_name} has been banned (${banDurationType === 'permanent' ? 'Permanent' : `${banDurationValue} ${banDurationType}`})`);
      setIsBanModalOpen(false);
      fetchUsersAndWallets();
    } catch (err) {
      showToast("Action failed: " + (err.response?.data?.message || err.message), 'error');
    } finally {
      setBanLoading(false);
    }
  };

  const handleUnban = async (user) => {
    const targetRank = ROLE_RANKS[user?.user_type] || 10;
    if (callerRank <= targetRank) {
      showToast(`Permission denied: You cannot unban an account with rank equal to or higher than your own (${user?.user_type}).`, 'error');
      return;
    }
    if (window.confirm(`Are you sure you want to unban ${user.first_name}?`)) {
      try {
        await usersAPI.banUser(user.id, { ban: false });
        showToast(`User ${user.first_name} unbanned successfully`);
        fetchUsersAndWallets();
      } catch (err) {
        showToast("Action failed: " + (err.response?.data?.message || err.message), 'error');
      }
    }
  };

  // DIRECT ROLE CHANGE (1-Click from Table)
  const handleDirectRoleChange = async (user, newRole) => {
    if (user.user_type === newRole) return;
    const targetRank = ROLE_RANKS[user.user_type] || 10;
    const newRank = ROLE_RANKS[newRole] || 10;

    if (callerRank <= targetRank) {
      showToast(`Permission denied: You cannot modify a user with rank equal to or higher than your own (${user.user_type}).`, 'error');
      return;
    }
    if (callerRank <= newRank) {
      showToast(`Permission denied: You cannot assign a role equal to or higher than your own (${newRole}).`, 'error');
      return;
    }

    const isAgency = user.user_type === 'AGENCY' || user.is_agency;
    const isBD = user.user_type === 'BD';
    const isManager = user.user_type === 'MANAGER';

    // 1. If converting from AGENCY to another role, offer host transfer selection
    if (isAgency && newRole !== 'AGENCY') {
      const wantToTransfer = window.confirm(
        `This user owns an agency. Converting them to ${newRole} will deactivate their agency.\n\n` +
        `Would you like to select a target agency to transfer their existing hosts to?\n\n` +
        `• Click OK to open the transfer panel and select the destination agency.\n` +
        `• Click Cancel to proceed with changing role without transferring hosts.`
      );
      if (wantToTransfer) {
        openEditModal(user, newRole);
        return;
      }
    } else if (isBD && newRole !== 'BD') {
      // 2. If converting from BD to another role, offer agency transfer selection
      const wantToTransfer = window.confirm(
        `This user is currently a BD with assigned agencies. Converting them to ${newRole} will deactivate their BD profile.\n\n` +
        `Would you like to select a target BD to transfer their assigned agencies to?\n\n` +
        `• Click OK to open the transfer panel and select the destination BD.\n` +
        `• Click Cancel to proceed with changing role without transferring agencies.`
      );
      if (wantToTransfer) {
        openEditModal(user, newRole);
        return;
      }
    } else if (isManager && newRole !== 'MANAGER') {
      // 3. If converting from MANAGER to another role, offer BD transfer selection
      const wantToTransfer = window.confirm(
        `This user is currently an Executive Manager with assigned BDs. Converting them to ${newRole} will deactivate their Manager profile.\n\n` +
        `Would you like to select a target Manager to transfer their assigned BDs to?\n\n` +
        `• Click OK to open the transfer panel and select the destination Manager.\n` +
        `• Click Cancel to proceed with changing role without transferring BDs.`
      );
      if (wantToTransfer) {
        openEditModal(user, newRole);
        return;
      }
    } else {
      let confirmMsg = `Are you sure you want to change ${user.first_name || 'this user'}'s role from ${user.user_type} to ${newRole}?`;
      if (!window.confirm(confirmMsg)) return;
    }

    const payload = { user_type: newRole };

    // If changing to AGENCY, prompt Admin to enter the agency name
    if (newRole === 'AGENCY') {
      const defaultName = user.owned_agency_name || '';
      const enteredAgencyName = window.prompt(
        `Please enter the Agency Name for ${user.first_name || 'User'}:`,
        defaultName
      );
      if (enteredAgencyName === null) {
        // Admin cancelled
        return;
      }
      if (!enteredAgencyName.trim()) {
        showToast("Agency Name is required! Please enter the agency name.", "error");
        return;
      }
      payload.agency_name = enteredAgencyName.trim();
    }

    try {
      await usersAPI.updateUser(user.id, payload);
      showToast(`Role updated to ${newRole} successfully!`);
      fetchUsersAndWallets();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Error updating role', 'error');
    }
  };

  // DIRECT COIN SELLER TOGGLE (1-Click from Table)
  const handleToggleSellerDirect = async (user) => {
    const nextState = !user.is_seller;
    const actionText = nextState ? 'enable Coin Seller privileges for' : 'remove Coin Seller from';
    if (!window.confirm(`Are you sure you want to ${actionText} ${user.first_name || 'this user'}?`)) {
      return;
    }
    try {
      await usersAPI.updateUser(user.id, { is_seller: nextState });
      showToast(`Coin Seller ${nextState ? 'enabled' : 'disabled'} successfully!`);
      fetchUsersAndWallets();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Error updating seller status', 'error');
    }
  };

  // WARNINGS
  const handleOpenWarnModal = (user) => {
    setTargetUser(user);
    setWarnReason('');
    setIsWarnModalOpen(true);
  };

  const handleSendWarning = async (e) => {
    e.preventDefault();
    if (!targetUser || !warnReason.trim()) return;
    try {
      await axios.post(`${constant.API_BASE_URL}/admin/business/users/${targetUser.id}/warning`, { reason: warnReason.trim() }, {
        headers: { Authorization: token }
      });
      setIsWarnModalOpen(false);
      showToast("Warning recorded successfully.");
    } catch (err) {
      showToast("Failed to record warning: " + (err.response?.data?.message || err.message), 'error');
    }
  };

  // WALLET RESET
  const handleResetWallet = async (user) => {
    if (window.confirm(`WARNING: Are you sure you want to reset ${user.first_name}'s coin wallet to 0.00? This cannot be undone.`)) {
      try {
        await axios.put(`${constant.API_BASE_URL}/admin/business/users/${user.id}/wallet/reset`, {}, {
          headers: { Authorization: token }
        });
        showToast("Wallet reset successfully");
        fetchUsersAndWallets();
      } catch (err) {
        showToast("Wallet reset failed: " + (err.response?.data?.message || err.message), 'error');
      }
    }
  };

  // TRANSACTION LOGS WINDOW
  const handleOpenTxLogs = async (user) => {
    const initialWallet = walletsMap[user.id] || {};
    setTxUser({
      ...user,
      balance: Number(initialWallet.balance || 0),
      diamond_balance: Number(initialWallet.diamond_balance || 0)
    });
    setTxLogs([]);
    setIsTxModalOpen(true);
    setTxLoading(true);

    try {
      const [walletRes, txRes] = await Promise.all([
        walletsAPI.getWallet(user.id).catch(() => null),
        walletsAPI.getTransactions(user.id).catch(() => null)
      ]);

      const wData = walletRes?.data?.data || initialWallet;
      setTxUser({
        ...user,
        balance: Number(wData.balance || 0),
        diamond_balance: Number(wData.diamond_balance || 0)
      });
      setTxLogs(txRes?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setTxLoading(false);
    }
  };

  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    const isUserBanned = user.is_ban !== undefined 
      ? Boolean(user.is_ban) 
      : Boolean(!user.is_active && (!user.banned_until || new Date(user.banned_until) > new Date()));

    if (statusFilter === 'ACTIVE' && isUserBanned) return false;
    if (statusFilter === 'BANNED' && !isUserBanned) return false;
    if (roleFilter !== 'ALL') {
      if (roleFilter === 'SELLER') {
        if (!user.is_seller) return false;
      } else if (user.user_type !== roleFilter) {
        return false;
      }
    }
    if (countryFilter !== 'ALL') {
      if (!user.country || user.country.toLowerCase() !== countryFilter.toLowerCase()) {
        return false;
      }
    }
    
    const term = searchTerm.toLowerCase();
    const fullName = (user.first_name + ' ' + user.last_name).toLowerCase();
    const userName = (user.user_name || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    const userIdStr = (user.id || '').toString().toLowerCase();
    const userCustomId = (user.user_id || '').toLowerCase();
    const xoxoIdStr = (user.xoxo_id || '').toString().toLowerCase();
    const countryStr = (user.country || '').toLowerCase();
    
    return fullName.includes(term) || 
           userName.includes(term) || 
           email.includes(term) || 
           userIdStr.includes(term) ||
           userCustomId.includes(term) ||
           xoxoIdStr.includes(term) ||
           countryStr.includes(term);
  }) : [];

  const handleRefresh = () => {
    setSearchTerm('');
    setStatusFilter('ALL');
    setRoleFilter('ALL');
    setCountryFilter('ALL');
    fetchUsersAndWallets();
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-extrabold text-slate-800">User Management</h2>
          <p className="text-slate-500 mt-1">Audit platform accounts, warning counts, ban statuses, and real-time wallet balances.</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={handleRefresh}
            className="flex items-center gap-1.5 bg-white text-slate-700 px-4 py-2 text-sm rounded-xl font-semibold shadow-sm border border-gray-200 hover:bg-gray-50 transition active:scale-95 cursor-pointer"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh Accounts
          </button>
          <button
            onClick={openAddModal}
            className="flex items-center gap-1.5 bg-pink-600 text-white px-4 py-2 text-sm rounded-xl font-semibold shadow-lg shadow-pink-500/30 hover:bg-pink-700 transition active:scale-95 cursor-pointer"
          >
            <UserPlus size={16} /> Add New User
          </button>
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-[24px] shadow-xl shadow-slate-100/30 border border-slate-100 overflow-hidden">

        {/* Table Toolbar */}
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4 w-full sm:w-auto">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search user accounts..."
                className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition font-semibold text-slate-600 text-sm h-10 cursor-pointer"
            >
              <option value="ALL">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="BANNED">Banned</option>
            </select>
            <select 
              value={roleFilter} 
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-pink-500/20 transition font-semibold text-slate-600 text-sm h-10 cursor-pointer"
            >
              <option value="ALL">All Roles</option>
              <option value="OWNER">👑 Owner</option>
              <option value="SUPER_ADMIN">Super Admin</option>
              <option value="ADMIN">Admin</option>
              <option value="AGENCY">Agency</option>
              <option value="HOST">Host</option>
              <option value="BD">BD</option>
              <option value="MANAGER">Manager</option>
              <option value="SELLER">🪙 Coin Seller</option>
              <option value="USER">User</option>
            </select>
            <div className="w-48">
              <FilterCountrySelect 
                value={countryFilter} 
                onChange={(val) => setCountryFilter(val)} 
              />
            </div>
          </div>
          <div className="text-sm font-bold text-slate-500 bg-slate-50 px-4 py-2 rounded-lg border border-slate-100">
            Total Records: <span className="text-pink-600">{filteredUsers.length}</span>
          </div>
        </div>

        {/* Table Content */}
        {loading ? (
          <div className="p-20 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-slate-500">Loading user accounts...</p>
          </div>
        ) : error ? (
          <div className="p-20 text-center text-red-500">
            {error}
            <button onClick={fetchUsersAndWallets} className="block mx-auto mt-4 text-pink-600 font-bold underline">Try Again</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/50 text-slate-400 text-xs font-bold uppercase tracking-wider">
                  <th className="p-6">User Profile</th>
                  <th className="p-6">Contact Info</th>
                  <th className="p-6">Location</th>
                  <th className="p-6">Wallet Balance</th>
                  <th className="p-6">Level & Tier</th>
                  <th className="p-6">User Type</th>
                  <th className="p-6">Status</th>
                  <th className="p-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-slate-50/50 transition duration-150 group">
                    <td className="p-6">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 font-bold text-lg border-2 border-white shadow-sm shrink-0">
                          {user.first_name?.[0] || 'U'}{user.last_name?.[0] || ''}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-bold text-slate-800 text-base">{user.first_name} {user.last_name}</p>
                            {Boolean(user.is_agency || user.user_type === 'AGENCY') && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-amber-100 text-amber-800 border border-amber-300">
                                AGENCY
                              </span>
                            )}
                            {Boolean(user.is_host || user.user_type === 'HOST') && (
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-extrabold bg-pink-100 text-pink-700 border border-pink-300">
                                HOST
                              </span>
                            )}
                          </div>
                          <p className="text-xs font-extrabold text-pink-600">XOXO ID: {user.xoxo_id || user.user_id || 'N/A'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-6">
                      <p className="text-slate-600 font-semibold">{user.email}</p>
                      <p className="text-xs text-slate-400 mt-1">{user.phone || 'No phone'}</p>
                    </td>
                    <td className="p-6">
                      <CountryBadge country={user.country} />
                    </td>
                    <td className="p-6 font-extrabold text-slate-800 text-base">
                      {walletsMap[user.id] ? walletsMap[user.id].balance?.toLocaleString() : '0'} Coins
                      {walletsMap[user.id] && walletsMap[user.id].diamond_balance > 0 && <p className="text-xs text-pink-500 font-bold mt-1">💎 {walletsMap[user.id].diamond_balance?.toLocaleString()} Diamonds</p>}
                      {user.diamond_salary && <p className="text-xs text-blue-500 font-bold mt-1">💰 {user.diamond_salary} Salary</p>}
                    </td>
                    <td className="p-6">
                      <div className="flex flex-col gap-1.5">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black bg-amber-50 text-amber-800 border border-amber-200/70 shadow-xs w-fit">
                          👑 Wealth Lv {user.wealth_level || 1}
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-black bg-pink-50 text-pink-700 border border-pink-200/70 shadow-xs w-fit">
                          💖 Charm Lv {user.charm_level || 1}
                        </span>
                      </div>
                    </td>
                    <td className="p-6">
                      <div className="flex flex-col gap-1.5 items-start">
                        {/* Direct 1-Click Role Switcher Dropdown OR Protected Role Badge */}
                        {(() => {
                          const targetRank = ROLE_RANKS[user.user_type] || 10;
                          const canChangeRole = callerRank > targetRank;

                          if (!canChangeRole) {
                            return (
                              <div
                                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-black uppercase border shadow-xs select-none ${
                                  user.user_type === 'OWNER'
                                    ? 'bg-amber-100 text-amber-900 border-amber-300'
                                    : user.user_type === 'SUPER_ADMIN'
                                    ? 'bg-rose-50 text-rose-800 border-rose-300'
                                    : 'bg-slate-100 text-slate-600 border-slate-300'
                                }`}
                                title={`Protected role (${user.user_type}). Accounts of equal or lower rank cannot modify this role.`}
                              >
                                {user.user_type === 'OWNER' ? (
                                  <span className="flex items-center gap-1 text-amber-800"><Crown size={12} className="text-amber-600 fill-amber-500" /> OWNER</span>
                                ) : (
                                  <span className="flex items-center gap-1"><Lock size={11} className="text-slate-400" /> {user.user_type}</span>
                                )}
                              </div>
                            );
                          }

                          const assignableRoles = ['USER', 'HOST', 'AGENCY', 'BD', 'MANAGER', 'ADMIN', 'SUPER_ADMIN', 'OWNER']
                            .filter(r => ROLE_RANKS[r] < callerRank);

                          return (
                            <div className="relative inline-block">
                              <select
                                value={user.user_type}
                                onChange={(e) => handleDirectRoleChange(user, e.target.value)}
                                className={`appearance-none pl-3 pr-7 py-1 rounded-full text-xs font-black uppercase border cursor-pointer outline-none transition shadow-xs ${
                                  user.user_type === 'OWNER' ? 'bg-amber-100 text-amber-900 border-amber-300 hover:bg-amber-200' :
                                  user.user_type === 'SUPER_ADMIN' ? 'bg-rose-50 text-rose-700 border-rose-300 hover:bg-rose-100' :
                                  user.user_type === 'ADMIN' ? 'bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100' :
                                  user.user_type === 'AGENCY' ? 'bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100' :
                                  user.user_type === 'HOST' ? 'bg-pink-50 text-pink-700 border-pink-300 hover:bg-pink-100' :
                                  user.user_type === 'BD' ? 'bg-indigo-50 text-indigo-700 border-indigo-300 hover:bg-indigo-100' :
                                  user.user_type === 'MANAGER' ? 'bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100' :
                                  'bg-slate-50 text-slate-700 border-slate-300 hover:bg-slate-100'
                                }`}
                                title="Directly change user role"
                              >
                                <option value={user.user_type} disabled>{user.user_type} (Current)</option>
                                {assignableRoles.map(r => (
                                  <option key={r} value={r}>{r}</option>
                                ))}
                              </select>
                              <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[9px] opacity-60">▼</span>
                            </div>
                          );
                        })()}

                        {/* Direct 1-Click Coin Seller Toggle */}
                        <button
                          type="button"
                          onClick={() => handleToggleSellerDirect(user)}
                          className={`px-2 py-0.5 rounded-md text-[10px] font-black border shadow-xs inline-flex items-center gap-1 transition cursor-pointer active:scale-95 ${
                            user.is_seller
                              ? 'bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-950 border-amber-400 hover:brightness-105'
                              : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-amber-50 hover:text-amber-700 hover:border-amber-200'
                          }`}
                          title={user.is_seller ? 'Click to disable Coin Seller' : 'Click to enable Coin Seller'}
                        >
                          <span>🪙</span> {user.is_seller ? 'SELLER (ON)' : '+ Make Seller'}
                        </button>

                        {/* Affiliation Subtitles */}
                        {user.user_type === 'HOST' && user.host_agency_name && (
                          <span className="text-[10px] text-pink-600 font-semibold truncate max-w-[150px]" title={user.host_agency_name}>
                            🏢 {user.host_agency_name}
                          </span>
                        )}
                        {user.user_type === 'AGENCY' && (user.owned_agency_code || user.agency_code) && (
                          <span className="text-[10px] text-amber-700 font-semibold truncate max-w-[150px]">
                            #{user.owned_agency_code || user.agency_code} {user.owned_agency_name || ''}
                          </span>
                        )}
                        {user.user_type === 'BD' && user.manager_name && user.manager_name.trim() && (
                          <span className="text-[10px] text-indigo-600 font-semibold truncate max-w-[150px]">
                            👤 Mgr: {user.manager_name}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-6">
                      {(() => {
                        const isCurrentlyBanned = user.is_ban !== undefined
                          ? Boolean(user.is_ban)
                          : Boolean(!user.is_active && (!user.banned_until || new Date(user.banned_until) > new Date()));
                        return (
                          <div className="flex flex-col gap-1">
                            <span className={`
                              inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide w-fit
                              ${!isCurrentlyBanned ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}
                            `}>
                              <span className={`w-1.5 h-1.5 rounded-full ${!isCurrentlyBanned ? 'bg-green-500' : 'bg-red-500'}`}></span>
                              {!isCurrentlyBanned ? 'Active' : 'Banned'}
                            </span>
                            {isCurrentlyBanned && (
                              <div className="text-[11px] text-slate-500 space-y-0.5 mt-0.5 max-w-[180px]">
                                <p className="font-bold text-rose-600">
                                  {user.banned_until ? `Until: ${new Date(user.banned_until).toLocaleDateString()} ${new Date(user.banned_until).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Permanent Ban'}
                                </p>
                                {user.ban_reason && (
                                  <p className="text-slate-400 italic line-clamp-1" title={user.ban_reason}>
                                    Reason: "{user.ban_reason}"
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="p-6 text-right">
                      <div className="flex justify-end gap-1.5 flex-wrap max-w-[250px] ml-auto">
                        <button 
                          onClick={() => handleOpenLevelModal(user)}
                          className="px-2 py-1 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-md text-[10px] font-black transition flex items-center gap-1 shadow-xs cursor-pointer active:scale-95"
                          title="Edit User Level & Tier Rewards"
                        >
                          <Sparkles size={12} /> Level
                        </button>
                        <button 
                          onClick={() => handleOpenCustomIdModal(user)}
                          className="px-2 py-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-md text-[10px] font-black transition flex items-center gap-1 shadow-xs cursor-pointer active:scale-95"
                          title="Customize or Generate VIP User ID"
                        >
                          <Award size={12} /> ID
                        </button>
                        <button 
                          onClick={() => handleOpenTxLogs(user)}
                          className="px-2 py-1 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-md text-[10px] font-bold transition flex items-center gap-1 cursor-pointer"
                          title="View Transaction History"
                        >
                          <Wallet size={12} /> History
                        </button>
                        <button 
                          onClick={() => handleOpenWarnModal(user)}
                          className="px-2 py-1 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 rounded-md text-[10px] font-bold transition border border-yellow-100 flex items-center gap-1 cursor-pointer"
                          title="Issue System Warning"
                        >
                          <AlertTriangle size={12} /> Warn
                        </button>
                        {!(user.is_ban !== undefined ? Boolean(user.is_ban) : (!user.is_active && (!user.banned_until || new Date(user.banned_until) > new Date()))) ? (
                          <button 
                            onClick={() => handleOpenBanModal(user)}
                            className="px-2 py-1 bg-red-50 text-red-600 hover:bg-red-100 border border-red-100 rounded-md text-[10px] font-bold transition flex items-center gap-1 cursor-pointer"
                            title="Ban User (Hourly / Daily / Permanent)"
                          >
                            <Ban size={12} /> Ban
                          </button>
                        ) : (
                          <button 
                            onClick={() => handleUnban(user)}
                            className="px-2 py-1 bg-green-50 text-green-600 hover:bg-green-100 border border-green-100 rounded-md text-[10px] font-bold transition flex items-center gap-1 cursor-pointer"
                            title="Unban User Account"
                          >
                            <CheckCircle size={12} /> Unban
                          </button>
                        )}
                        <button 
                          onClick={() => handleResetWallet(user)}
                          className="px-2 py-1 bg-slate-900 text-white hover:bg-slate-800 rounded-md text-[10px] font-bold transition flex items-center gap-1 cursor-pointer"
                          title="Reset Wallet Balance to 0"
                        >
                          <Clock size={12} /> Reset
                        </button>
                        {/* Edit Profile */}
                        {(callerRank > (ROLE_RANKS[user.user_type] || 10) || user.id === userinfo?.id) && (
                          <button
                            onClick={() => openEditModal(user)}
                            className="p-1.5 hover:bg-blue-50 text-gray-400 hover:text-blue-600 rounded-md transition border border-gray-200 hover:border-blue-200 cursor-pointer" 
                            title="Edit Profile"
                          >
                            <Edit2 size={12} />
                          </button>
                        )}

                        {/* Revoke Role (demote to USER) */}
                        {(user.user_type === 'SUPER_ADMIN' || user.user_type === 'ADMIN') && callerRank > (ROLE_RANKS[user.user_type] || 10) && (
                          <button
                            onClick={() => handleStripRole(user)}
                            className="px-2 py-1 bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200 rounded-md text-[10px] font-black transition flex items-center gap-1 cursor-pointer active:scale-95"
                            title="Revoke Admin privileges and demote to regular User"
                          >
                            <ShieldAlert size={12} /> Revoke Role
                          </button>
                        )}

                        {/* Delete User */}
                        {callerRank > (ROLE_RANKS[user.user_type] || 10) && (
                          <button
                            onClick={() => handleDelete(user)}
                            className="p-1.5 hover:bg-rose-50 text-gray-400 hover:text-rose-600 rounded-md transition border border-gray-200 hover:border-rose-200 cursor-pointer"
                            title="Permanently Delete User"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr>
                    <td colSpan="6" className="p-10 text-center text-gray-500">No users found matching your search.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Edit / Add Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
              <h3 className="text-xl font-bold text-slate-800">{editingUser ? 'Edit User' : 'Add New User'}</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50 transition">
                <X size={24} />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
              {/* Scrollable Fields Body */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 uppercase">First Name</label>
                    <input
                      required
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-500 uppercase">Last Name</label>
                    <input
                      required
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Email Address</label>
                  <input
                    required
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                  />
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-end">
                    <label className="text-xs font-bold text-slate-500 uppercase">Password {editingUser && '(Leave blank to keep same)'}</label>
                    <span className={formData.password.length >= 6 ? "text-[10px] font-bold text-green-500" : "text-[10px] font-bold text-slate-400"}>Min 6 chars</span>
                  </div>
                  <div className="relative">
                    <input
                      required={!editingUser}
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full pl-4 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Phone</label>
                  <input
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Diamond Salary</label>
                  <input
                    type="number"
                    name="diamond_salary"
                    value={formData.diamond_salary}
                    onChange={handleInputChange}
                    placeholder="e.g. 5000"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Country / Location</label>
                  <SearchableCountrySelect
                    value={formData.country}
                    onChange={(val) => setFormData({ ...formData, country: val })}
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs font-bold text-slate-500 uppercase">Primary User Role</label>
                  <select
                    required
                    name="user_type"
                    value={formData.user_type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition font-semibold"
                  >
                    {['USER', 'HOST', 'AGENCY', 'BD', 'MANAGER', 'ADMIN', 'SUPER_ADMIN', 'OWNER']
                      .filter(r => {
                        if (editingUser) {
                          return ROLE_RANKS[r] < callerRank || r === formData.user_type;
                        }
                        return ROLE_RANKS[r] < callerRank;
                      })
                      .map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))
                    }
                  </select>
                </div>

                {/* Conditional Field: HOST -> Select Agency */}
                {formData.user_type === 'HOST' && (
                  <div className="p-3.5 bg-pink-50/60 rounded-2xl border border-pink-200 space-y-1.5 animate-in fade-in duration-200">
                    <label className="text-xs font-bold text-pink-700 uppercase flex items-center gap-1.5">
                      <Building2 size={14} /> Assign Under Agency
                    </label>
                    <select
                      name="agency_id"
                      value={formData.agency_id}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 bg-white border border-pink-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition text-sm font-semibold text-slate-700"
                    >
                      <option value="">-- Select Agency --</option>
                      {agenciesList.map(ag => (
                        <option key={ag.id} value={ag.id}>
                          #{ag.agency_code} - {ag.agency_name}
                        </option>
                      ))}
                    </select>
                    <p className="text-[11px] text-pink-600">
                      Linking host to an agency approves their host status automatically.
                    </p>
                  </div>
                )}

                {/* Conditional Field: AGENCY -> Agency Name & BD */}
                {formData.user_type === 'AGENCY' && (
                  <div className="p-3.5 bg-amber-50/60 rounded-2xl border border-amber-200 space-y-3 animate-in fade-in duration-200">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <label className="text-xs font-bold text-amber-800 uppercase flex items-center gap-1.5">
                          <Building2 size={14} /> Agency Name (Manual Entry)
                        </label>
                        <span className="text-[10px] text-amber-700 font-extrabold uppercase bg-amber-100 px-1.5 py-0.5 rounded">
                          Required
                        </span>
                      </div>
                      <input
                        required={formData.user_type === 'AGENCY'}
                        name="agency_name"
                        value={formData.agency_name}
                        onChange={handleInputChange}
                        placeholder={editingUser?.owned_agency_name || "Enter official Agency Name"}
                        className="w-full px-4 py-2.5 bg-white border border-amber-300 rounded-xl focus:ring-2 focus:ring-amber-500/20 outline-none text-sm font-semibold"
                      />
                      <p className="text-[11px] text-amber-700 font-medium">
                        A unique numeric Agency Code (e.g. 1004) will be generated automatically. Please enter the official Agency Name.
                      </p>
                    </div>

                    <div className="space-y-1">
                      <label className="text-xs font-bold text-amber-800 uppercase">Assign Under BD (Optional)</label>
                      <select
                        name="bd_user_id"
                        value={formData.bd_user_id}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2.5 bg-white border border-amber-200 rounded-xl focus:ring-2 focus:ring-amber-500/20 outline-none text-sm font-medium"
                      >
                        <option value="">-- No BD Assigned --</option>
                        {users.filter(u => isBdActiveAndApproved(u, editingUser?.id)).map(bd => (
                          <option key={bd.id} value={bd.id}>
                            {bd.first_name} {bd.last_name} (ID: {bd.xoxo_id || bd.user_id})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Conditional Field: Old Agency Deactivation & Transfer when changing role from AGENCY */}
                {editingUser && (editingUser.user_type === 'AGENCY' || editingUser.owned_agency_id) && formData.user_type !== 'AGENCY' && (
                  <div className="p-4 bg-red-50 rounded-2xl border border-red-200 space-y-2.5 animate-in fade-in duration-200">
                    <div className="flex items-center gap-2 text-red-800 font-bold text-xs uppercase">
                      <AlertTriangle size={16} className="text-red-600" />
                      Agency Deactivation & Host Transfer
                    </div>
                    <p className="text-xs text-red-700 leading-relaxed">
                      This user is currently the owner of <strong>{editingUser.owned_agency_name || `#${editingUser.owned_agency_code}`}</strong>. Changing role will deactivate their agency.
                    </p>
                    <div className="space-y-1 pt-1">
                      <label className="text-[11px] font-bold text-red-900 uppercase block">Transfer Existing Hosts To Another Agency:</label>
                      <select
                        name="transfer_to_agency_id"
                        value={formData.transfer_to_agency_id}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2.5 bg-white border border-red-200 rounded-xl focus:ring-2 focus:ring-red-500/20 outline-none text-sm font-semibold text-slate-700"
                      >
                        <option value="">-- Do Not Transfer (Hosts unlinked) --</option>
                        {agenciesList.filter(ag => ag.id !== editingUser.owned_agency_id).map(ag => (
                          <option key={ag.id} value={ag.id}>
                            #{ag.agency_code} - {ag.agency_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Conditional Field: BD Agency Transfer when changing role from BD */}
                {editingUser && editingUser.user_type === 'BD' && formData.user_type !== 'BD' && (
                  <div className="p-4 bg-indigo-50 rounded-2xl border border-indigo-200 space-y-2.5 animate-in fade-in duration-200">
                    <div className="flex items-center gap-2 text-indigo-800 font-bold text-xs uppercase">
                      <AlertTriangle size={16} className="text-indigo-600" />
                      BD Deactivation & Agency Transfer
                    </div>
                    <p className="text-xs text-indigo-700 leading-relaxed">
                      This user is currently a <strong>Business Developer (BD)</strong>. Changing their role will deactivate their BD profile.
                    </p>
                    <div className="space-y-1 pt-1">
                      <label className="text-[11px] font-bold text-indigo-900 uppercase block">Transfer Managed Agencies To Another BD:</label>
                      <select
                        name="transfer_to_bd_id"
                        value={formData.transfer_to_bd_id}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2.5 bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 outline-none text-sm font-semibold text-slate-700"
                      >
                        <option value="">-- Do Not Transfer (Agencies unlinked) --</option>
                        {users.filter(u => isBdActiveAndApproved(u, editingUser?.id)).map(bd => (
                          <option key={bd.id} value={bd.id}>
                            {bd.first_name} {bd.last_name} (ID: {bd.xoxo_id || bd.user_id})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Conditional Field: Manager BD Transfer when changing role from MANAGER */}
                {editingUser && editingUser.user_type === 'MANAGER' && formData.user_type !== 'MANAGER' && (
                  <div className="p-4 bg-blue-50 rounded-2xl border border-blue-200 space-y-2.5 animate-in fade-in duration-200">
                    <div className="flex items-center gap-2 text-blue-800 font-bold text-xs uppercase">
                      <AlertTriangle size={16} className="text-blue-600" />
                      Manager Deactivation & BD Transfer
                    </div>
                    <p className="text-xs text-blue-700 leading-relaxed">
                      This user is currently an <strong>Executive Manager</strong>. Changing their role will deactivate their Manager profile.
                    </p>
                    <div className="space-y-1 pt-1">
                      <label className="text-[11px] font-bold text-blue-900 uppercase block">Transfer Managed BDs To Another Manager:</label>
                      <select
                        name="transfer_to_manager_id"
                        value={formData.transfer_to_manager_id}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2.5 bg-white border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none text-sm font-semibold text-slate-700"
                      >
                        <option value="">-- Do Not Transfer (BDs unlinked) --</option>
                        {users.filter(u => u.user_type === 'MANAGER' && u.id !== editingUser.id).map(mgr => (
                          <option key={mgr.id} value={mgr.id}>
                            {mgr.first_name} {mgr.last_name} (ID: {mgr.xoxo_id || mgr.user_id})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Conditional Field: BD -> Assign Under Manager */}
                {formData.user_type === 'BD' && (
                  <div className="p-3.5 bg-indigo-50/60 rounded-2xl border border-indigo-200 space-y-1.5 animate-in fade-in duration-200">
                    <label className="text-xs font-bold text-indigo-700 uppercase flex items-center gap-1.5">
                      <UserCheck size={14} /> Assign Under Manager (Optional)
                    </label>
                    <select
                      name="manager_id"
                      value={formData.manager_id}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2.5 bg-white border border-indigo-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 outline-none text-sm font-semibold text-slate-700"
                    >
                      <option value="">-- No Manager Assigned --</option>
                      {users.filter(u => u.user_type === 'MANAGER').map(mgr => (
                        <option key={mgr.id} value={mgr.id}>
                          {mgr.first_name} {mgr.last_name} (ID: {mgr.xoxo_id || mgr.user_id})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Dual Power: Coin Seller Toggle */}
                <div className="p-4 bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 border border-amber-200 rounded-2xl flex items-center justify-between shadow-xs">
                  <div className="space-y-0.5 pr-3">
                    <div className="flex items-center gap-2">
                      <span className="text-base">🪙</span>
                      <span className="text-sm font-bold text-slate-800">Official Coin Seller (Dual Power)</span>
                      {formData.is_seller && (
                        <span className="text-[10px] bg-amber-500 text-white font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider">
                          Active
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 font-medium">
                      Allow this account to log in to the Coin Seller portal to sell & transfer coins without changing their primary role.
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer shrink-0">
                    <input
                      type="checkbox"
                      name="is_seller"
                      checked={formData.is_seller}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                  </label>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-6 bg-slate-50 border-t border-slate-100 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    className="w-4 h-4 rounded text-pink-600 focus:ring-pink-500 border-slate-300 cursor-pointer"
                  />
                  <label htmlFor="is_active" className="text-sm font-semibold text-slate-700 cursor-pointer select-none">
                    User is Active
                  </label>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-6 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-100 rounded-xl transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 px-8 py-2.5 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 shadow-lg shadow-slate-200 transition active:scale-95"
                  >
                    <Save size={18} /> {editingUser ? 'Save Changes' : 'Create User'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Warning Message Modal */}
      {isWarnModalOpen && targetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <AlertTriangle className="text-yellow-500" size={20} /> Issue Warning
              </h3>
              <button onClick={() => setIsWarnModalOpen(false)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSendWarning} className="p-6 space-y-4">
              <p className="text-xs text-slate-500 font-semibold">
                Warning details will be recorded as a Security Alert log linked to {targetUser.first_name}'s account.
              </p>
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-400 uppercase">Warning Reason</label>
                <textarea
                  required
                  rows={3}
                  placeholder="e.g. Terms violation or suspicious login pattern observed."
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-pink-500/20 outline-none transition text-sm font-medium"
                  value={warnReason}
                  onChange={(e) => setWarnReason(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsWarnModalOpen(false)}
                  className="px-5 py-2 bg-slate-100 text-slate-600 rounded-xl text-xs font-bold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-xl text-xs font-bold active:scale-95 transition"
                >
                  Issue Alert
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Transaction Logs Modal (Coins & Diamonds Statement) */}
      {isTxModalOpen && txUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[32px] shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden border border-slate-100">
            
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between shrink-0 bg-gradient-to-r from-slate-900 to-indigo-950 text-white">
              <div className="flex items-center gap-3">
                <img 
                  src={txUser.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(txUser.first_name || 'User')}&background=fce7f3&color=db2777`}
                  alt="Avatar"
                  className="w-12 h-12 rounded-full object-cover border-2 border-pink-500"
                />
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-black">{txUser.first_name} {txUser.last_name || ''}</h3>
                    <span className="px-2.5 py-0.5 bg-pink-500 text-white font-extrabold text-[11px] rounded-full tracking-wider">
                      XOXO ID: {txUser.xoxo_id || txUser.user_id || 'N/A'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-300 mt-0.5">{txUser.email}</p>
                </div>
              </div>

              {/* Balance Badges */}
              <div className="flex items-center gap-2.5">
                <div className="bg-white/10 px-3.5 py-1.5 rounded-xl border border-white/15 text-right">
                  <p className="text-[9px] uppercase font-bold text-amber-300">Coins Balance</p>
                  <p className="text-sm font-black text-white">🪙 {Number(txUser.balance || 0).toLocaleString()}</p>
                </div>
                <div className="bg-white/10 px-3.5 py-1.5 rounded-xl border border-white/15 text-right">
                  <p className="text-[9px] uppercase font-bold text-pink-300">Diamonds Balance</p>
                  <p className="text-sm font-black text-pink-300">💎 {Number(txUser.diamond_balance || 0).toLocaleString()}</p>
                </div>
                <button onClick={() => setIsTxModalOpen(false)} className="text-slate-400 hover:text-white p-1.5 rounded-xl hover:bg-white/10 transition ml-2">
                  <X size={22} />
                </button>
              </div>
            </div>
            
            {/* Modal Body: Statement Table */}
            <div className="flex-1 overflow-y-auto p-6 bg-slate-50 space-y-3">
              {txLoading ? (
                <div className="p-20 text-center">
                  <div className="animate-spin w-10 h-10 border-4 border-pink-500 border-t-transparent rounded-full mx-auto mb-3"></div>
                  <p className="text-xs font-bold text-slate-500">Loading complete transaction ledger...</p>
                </div>
              ) : txLogs.length === 0 ? (
                <div className="p-20 text-center text-slate-400">
                  <Wallet size={48} className="mx-auto mb-3 opacity-30" />
                  <p className="font-bold text-slate-600">No activity recorded for this user.</p>
                  <p className="text-xs mt-1">This user has no past recharges, gifts, rewards, or deductions.</p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  <div className="flex justify-between items-center px-1 mb-1">
                    <p className="text-xs font-black uppercase tracking-wider text-slate-400">
                      Audit Statement ({txLogs.length} Records)
                    </p>
                    <span className="text-[11px] text-slate-500 font-semibold">Real-time ledger entries</span>
                  </div>

                  {txLogs.map((tx) => {
                    const txType = (tx.transaction_type || '').toUpperCase();
                    const isDiamond = (tx.currency_type || '').toUpperCase() === 'DIAMOND';
                    const isCredit = txType === 'COIN_ADD' || 
                                    txType === 'SELF_RECHARGE' || 
                                    txType === 'FRIEND_RECHARGE' || 
                                    txType === 'ROOM_BONUS_CLAIM' || 
                                    txType === 'TARGET_REWARD' ||
                                    (tx.balance_after !== undefined && tx.balance_before !== undefined && Number(tx.balance_after) > Number(tx.balance_before));
                    
                    return (
                      <div key={tx.id} className="p-4 bg-white rounded-2xl border border-slate-200/80 flex items-center justify-between shadow-sm hover:shadow-md transition">
                        <div className="flex items-center gap-3.5">
                          <div className={`p-2.5 rounded-xl ${
                            isCredit ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                          }`}>
                            {isCredit ? <Clock size={16} /> : <Ban size={16} />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wide border ${
                                isDiamond ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                              }`}>
                                {isDiamond ? '💎 DIAMOND' : '🪙 COIN'}
                              </span>
                              <span className="text-xs font-black text-slate-800 uppercase">{tx.transaction_type}</span>
                              <span className="text-[10px] text-slate-400 font-mono">TXN-{String(tx.id).substring(0, 8)}</span>
                            </div>
                            <p className="text-xs font-bold text-slate-700 mt-1">{tx.reason || 'Transaction executed'}</p>
                            <p className="text-[10px] text-slate-400 mt-0.5">{new Date(tx.created_at || tx.timestamp).toLocaleString()}</p>
                          </div>
                        </div>

                        <div className="text-right">
                          <p className={`text-base font-black ${isCredit ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {isCredit ? '+' : '-'}{Math.abs(Number(tx.amount || 0)).toLocaleString()} {isDiamond ? '💎' : '🪙'}
                          </p>
                          <div className="text-[10px] font-semibold text-slate-500 mt-0.5 bg-slate-100 px-2 py-0.5 rounded-md">
                            {isDiamond ? '💎' : '🪙'} {Number(tx.balance_before || 0).toLocaleString()} → {Number(tx.balance_after || 0).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end shrink-0">
              <button 
                onClick={() => setIsTxModalOpen(false)}
                className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold transition shadow-sm"
              >
                Close Statement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 👑 EDIT USER LEVEL & TIER REWARDS MODAL */}
      {isLevelModalOpen && levelUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[28px] w-full max-w-xl overflow-hidden shadow-2xl border border-slate-100 flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="p-6 bg-gradient-to-r from-purple-700 via-indigo-700 to-pink-600 text-white flex justify-between items-center shrink-0">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 rounded-2xl backdrop-blur-md">
                  <Sparkles size={24} className="text-amber-300 animate-pulse" />
                </div>
                <div>
                  <h3 className="font-extrabold text-xl">Edit User Level & Tier Rewards</h3>
                  <p className="text-purple-200 text-xs font-medium mt-0.5">
                    Adjust user level (1 - 100). Tier rewards unlock automatically!
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setIsLevelModalOpen(false)}
                className="p-2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSaveUserLevel} className="p-6 space-y-6 overflow-y-auto">
              
              {/* User Identity Card */}
              <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 flex items-center justify-between">
                <div className="flex items-center gap-3.5">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-black text-base border-2 border-white shadow-sm">
                    {levelUser.first_name?.[0] || 'U'}{levelUser.last_name?.[0] || ''}
                  </div>
                  <div>
                    <h4 className="font-black text-slate-800 text-base">{levelUser.first_name} {levelUser.last_name}</h4>
                    <p className="text-xs font-black text-purple-600 font-mono">XOXO ID: {levelUser.xoxo_id || levelUser.user_id}</p>
                  </div>
                </div>
                <div className="text-right">
                  <span className={`px-3 py-1 rounded-full text-xs font-extrabold uppercase ${
                    levelUser.user_type === 'AGENCY' ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                    levelUser.user_type === 'HOST' ? 'bg-pink-100 text-pink-700 border border-pink-300' :
                    'bg-slate-200/80 text-slate-700'
                  }`}>
                    {levelUser.user_type || 'USER'}
                  </span>
                </div>
              </div>

              {/* 👑 Wealth Level Selector */}
              <div className="p-5 bg-gradient-to-br from-amber-50/50 to-orange-50/30 rounded-2xl border border-amber-200/80 space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">👑</span>
                    <label className="font-black text-slate-800 text-sm">Wealth Level</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-black border ${getTierInfo(userWealthLevel).color}`}>
                      {getTierInfo(userWealthLevel).name} ({getTierInfo(userWealthLevel).range})
                    </span>
                    <span className="text-base font-black text-amber-700 bg-amber-100 px-3 py-0.5 rounded-xl">
                      Lv {userWealthLevel}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={userWealthLevel}
                    onChange={(e) => setUserWealthLevel(parseInt(e.target.value) || 1)}
                    className="w-full accent-amber-600 h-2 bg-slate-200 rounded-lg cursor-pointer"
                  />
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-[11px] text-slate-400 font-bold">Quick Presets:</span>
                    <div className="flex gap-1.5 flex-wrap">
                      {[1, 5, 10, 20, 40, 60, 80, 100].map((lvl) => (
                        <button
                          key={lvl}
                          type="button"
                          onClick={() => setUserWealthLevel(lvl)}
                          className={`px-2 py-0.5 rounded-md text-[11px] font-extrabold transition ${
                            userWealthLevel === lvl 
                              ? 'bg-amber-600 text-white shadow-xs' 
                              : 'bg-white text-slate-600 hover:bg-amber-100 border border-slate-200'
                          }`}
                        >
                          Lv {lvl}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* 💖 Charm Level Selector */}
              <div className="p-5 bg-gradient-to-br from-pink-50/50 to-rose-50/30 rounded-2xl border border-pink-200/80 space-y-3">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">💖</span>
                    <label className="font-black text-slate-800 text-sm">Charm Level</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-black border ${getTierInfo(userCharmLevel).color}`}>
                      {getTierInfo(userCharmLevel).name} ({getTierInfo(userCharmLevel).range})
                    </span>
                    <span className="text-base font-black text-pink-700 bg-pink-100 px-3 py-0.5 rounded-xl">
                      Lv {userCharmLevel}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={userCharmLevel}
                    onChange={(e) => setUserCharmLevel(parseInt(e.target.value) || 1)}
                    className="w-full accent-pink-600 h-2 bg-slate-200 rounded-lg cursor-pointer"
                  />
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-[11px] text-slate-400 font-bold">Quick Presets:</span>
                    <div className="flex gap-1.5 flex-wrap">
                      {[1, 5, 10, 20, 40, 60, 80, 100].map((lvl) => (
                        <button
                          key={lvl}
                          type="button"
                          onClick={() => setUserCharmLevel(lvl)}
                          className={`px-2 py-0.5 rounded-md text-[11px] font-extrabold transition ${
                            userCharmLevel === lvl 
                              ? 'bg-pink-600 text-white shadow-xs' 
                              : 'bg-white text-slate-600 hover:bg-pink-100 border border-slate-200'
                          }`}
                        >
                          Lv {lvl}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Automatic Backpack Rewards Alert */}
              <div className="p-4 bg-purple-50 rounded-2xl border border-purple-200 flex items-start gap-3">
                <Award size={20} className="text-purple-600 shrink-0 mt-0.5" />
                <div className="text-xs text-purple-900 leading-relaxed font-semibold">
                  <p className="font-extrabold text-purple-950 mb-0.5">Automatic Level Tier Backpack Rewards</p>
                  Saving new levels (e.g. 1 to 10 or 10 to 1) immediately unlocks the matching Avatar Frame & Chat Bubble into the user's backpack inventory and sends an in-app push notification to the user.
                </div>
              </div>

              {/* Modal Footer */}
              <div className="pt-2 flex justify-end gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsLevelModalOpen(false)}
                  className="px-5 py-2.5 border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-xl text-xs font-bold transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={levelSaving}
                  className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl text-xs font-extrabold transition flex items-center gap-2 shadow-md hover:shadow-lg active:scale-95 disabled:opacity-50 cursor-pointer"
                >
                  {levelSaving ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" /> Saving & Granting Rewards...
                    </>
                  ) : (
                    <>
                      <Sparkles size={14} /> Save Level & Unlock Rewards
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Ban Modal with Hourly, Days, and Permanent Options */}
      {isBanModalOpen && banTargetUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-md overflow-hidden border border-slate-100">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-rose-50/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center font-bold">
                  <Ban size={20} />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-800 text-lg">Ban User Account</h3>
                  <p className="text-xs text-slate-500">Suspending {banTargetUser.first_name} {banTargetUser.last_name}</p>
                </div>
              </div>
              <button 
                onClick={() => setIsBanModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleExecuteBan} className="p-6 space-y-5">
              {/* Quick Presets */}
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-2">
                  Quick Duration Presets
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: '1 Hour', type: 'hours', value: 1 },
                    { label: '24 Hours', type: 'hours', value: 24 },
                    { label: '7 Days', type: 'days', value: 7 },
                    { label: '15 Days', type: 'days', value: 15 },
                    { label: '30 Days', type: 'days', value: 30 },
                    { label: 'Permanent', type: 'permanent', value: 0 },
                  ].map((preset, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setBanDurationType(preset.type);
                        setBanDurationValue(preset.value);
                      }}
                      className={`px-3 py-2 text-xs font-bold rounded-xl border transition text-center ${
                        banDurationType === preset.type && (preset.type === 'permanent' || banDurationValue === preset.value)
                          ? 'bg-rose-600 text-white border-rose-600 shadow-sm'
                          : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Duration Selector */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">
                    Duration Unit
                  </label>
                  <select
                    value={banDurationType}
                    onChange={(e) => setBanDurationType(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                  >
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                    <option value="permanent">Permanent</option>
                  </select>
                </div>

                {banDurationType !== 'permanent' && (
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">
                      Quantity ({banDurationType})
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={banDurationValue}
                      onChange={(e) => setBanDurationValue(e.target.value)}
                      required
                      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                    />
                  </div>
                )}
              </div>

              {/* Reason */}
              <div>
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider block mb-1.5">
                  Ban Reason
                </label>
                <textarea
                  rows="3"
                  placeholder="e.g. Abusive behavior in room, harassment, illegal coin transactions..."
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                ></textarea>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsBanModalOpen(false)}
                  className="flex-1 py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-xl text-sm transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={banLoading}
                  className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl text-sm transition shadow-lg shadow-rose-500/20 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {banLoading ? <RefreshCw size={16} className="animate-spin" /> : <Ban size={16} />}
                  Confirm Ban
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Custom ID Modal */}
      {isCustomIdModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl border border-slate-100 overflow-hidden">
            <div className="p-6 bg-gradient-to-r from-amber-500 to-orange-500 text-white flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-xl backdrop-blur-md">
                  <Award size={22} className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-black tracking-tight">Customize User ID</h3>
                  <p className="text-xs text-amber-100 font-medium">Assign unique VIP XOXO ID or generate lucky numbers</p>
                </div>
              </div>
              <button 
                onClick={() => setIsCustomIdModalOpen(false)}
                className="text-white/80 hover:text-white transition p-1 hover:bg-white/10 rounded-lg cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={(e) => handleSaveCustomId(e, false)} className="p-6 space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between text-xs">
                <div>
                  <p className="font-bold text-slate-800">{customIdUser?.first_name} {customIdUser?.last_name}</p>
                  <p className="text-slate-500">{customIdUser?.email}</p>
                </div>
                <div className="text-right">
                  <span className="text-[10px] font-bold uppercase text-amber-600 block">Current ID</span>
                  <span className="font-black text-amber-800 text-sm">{customIdUser?.xoxo_id || customIdUser?.user_id || 'N/A'}</span>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 uppercase tracking-wider block mb-1.5">
                  New Custom XOXO ID
                </label>
                <input
                  type="text"
                  placeholder="e.g. 777777, 888888, 123456, 99999"
                  value={customIdValue}
                  onChange={(e) => setCustomIdValue(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-base font-black text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500/20 tracking-wider"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Must be unique across the platform. Minimum 4 characters.
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={(e) => handleSaveCustomId(e, true)}
                  disabled={customIdLoading}
                  className="flex-1 py-2.5 bg-amber-50 border border-amber-300 hover:bg-amber-100 text-amber-800 font-bold rounded-xl text-xs transition flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  <Sparkles size={14} className="text-amber-600" />
                  Auto-Gen VIP Lucky ID
                </button>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsCustomIdModalOpen(false)}
                  className="flex-1 py-2.5 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-xl text-sm transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={customIdLoading || !customIdValue.trim()}
                  className="flex-1 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold rounded-xl text-sm transition shadow-lg shadow-orange-500/20 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {customIdLoading ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                  Save Custom ID
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.visible && (
        <div className={`fixed bottom-4 right-4 z-[9999] px-6 py-3 rounded-xl shadow-lg border text-sm font-bold flex items-center gap-2 animate-in slide-in-from-bottom-5 fade-in duration-300 ${toast.type === 'error' ? 'bg-red-50 text-red-600 border-red-100' : 'bg-green-50 text-green-700 border-green-100'}`}>
          {toast.type === 'error' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default UsersManagement;